import React from 'react';
import TagBadge from './TagBadge';

/**
 * UserTagFilter
 * 
 * Responsibility: Provide tag-based filtering UI
 * Single Responsibility: Filter control for tag selection
 * 
 * @param {Array} availableTags - All tags that can be filtered by
 * @param {Set} selectedTagIds - Currently selected tag IDs for filtering
 * @param {Function} onTagSelectionChange - Callback (newSelectedTagIds: Set) => void
 */
export default function UserTagFilter({ availableTags, selectedTagIds, onTagSelectionChange }) {
    const handleTagClick = (tagId) => {
        const newSelection = new Set(selectedTagIds);
        if (newSelection.has(tagId)) {
            newSelection.delete(tagId);
        } else {
            newSelection.add(tagId);
        }
        onTagSelectionChange(newSelection);
    };

    const handleClearAll = () => {
        onTagSelectionChange(new Set());
    };

    if (!availableTags || availableTags.length === 0) {
        return null;
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <span style={styles.label}>Filter by tags:</span>
                {selectedTagIds.size > 0 && (
                    <button style={styles.clearButton} onClick={handleClearAll}>
                        Clear all
                    </button>
                )}
            </div>
            <div style={styles.tagGrid}>
                {availableTags.map((tag) => {
                    const isSelected = selectedTagIds.has(tag.tag_id);
                    return (
                        <button
                            key={tag.tag_id}
                            onClick={() => handleTagClick(tag.tag_id)}
                            style={{
                                ...styles.tagButton,
                                ...(isSelected && styles.tagButtonSelected),
                            }}
                            aria-pressed={isSelected}
                        >
                            <span
                                style={{
                                    ...styles.colorDot,
                                    backgroundColor: tag.tag_color,
                                }}
                            />
                            {tag.tag_name}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

const styles = {
    container: {
        marginBottom: 16,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    label: {
        fontSize: 14,
        fontWeight: 500,
        color: '#374151',
    },
    clearButton: {
        background: 'transparent',
        border: 'none',
        color: '#3b82f6',
        fontSize: 13,
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    tagGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    tagButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        backgroundColor: '#fff',
        border: '1px solid #d1d5db',
        borderRadius: 4,
        fontSize: 13,
        cursor: 'pointer',
        transition: 'all 0.15s',
    },
    tagButtonSelected: {
        backgroundColor: '#dbeafe',
        borderColor: '#3b82f6',
        fontWeight: 500,
    },
    colorDot: {
        width: 12,
        height: 12,
        borderRadius: '50%',
        border: '1px solid rgba(0,0,0,0.1)',
    },
};
